import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Tab,
  Tabs,
  Paper,
  Chip,
  Avatar,
  LinearProgress,
  Button,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Divider
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assessment as ProjectIcon,
  Group as TeamIcon,
  AccountBalance as FinancialIcon,
  TrendingUp,
  TrendingDown,
  Refresh,
  Download,
  FilterList,
  MoreVert
} from '@mui/icons-material';
import PerformanceDashboard from './PerformanceDashboard';
import ProjectReports from './ProjectReports';
import TeamProductivity from './TeamProductivity';
import FinancialReports from './FinancialReports';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `analytics-tab-${index}`,
    'aria-controls': `analytics-tabpanel-${index}`,
  };
}

interface KeyMetric {
  title: string;
  value: string;
  change: number;
  trend: 'up' | 'down' | 'neutral';
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
}

interface QuickStat {
  label: string;
  value: string;
  description: string;
}

const AnalyticsAndReportsPage: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    end: new Date()
  });
  const [refreshing, setRefreshing] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // Mock data for key metrics
  const keyMetrics: KeyMetric[] = [
    {
      title: 'Revenue Growth',
      value: '+24.5%',
      change: 24.5,
      trend: 'up',
      icon: <TrendingUp />,
      color: 'success'
    },
    {
      title: 'Active Projects',
      value: '47',
      change: 12.3,
      trend: 'up',
      icon: <ProjectIcon />,
      color: 'primary'
    },
    {
      title: 'Team Productivity',
      value: '92%',
      change: -2.1,
      trend: 'down',
      icon: <TeamIcon />,
      color: 'warning'
    },
    {
      title: 'Client Satisfaction',
      value: '4.8/5',
      change: 5.2,
      trend: 'up',
      icon: <DashboardIcon />,
      color: 'success'
    }
  ];

  const quickStats: QuickStat[] = [
    { label: 'Total Clients', value: '234', description: '+18 this month' },
    { label: 'Completed Projects', value: '156', description: '94% success rate' },
    { label: 'Revenue YTD', value: '$2.4M', description: '+32% vs last year' },
    { label: 'Team Members', value: '48', description: '6 new hires' }
  ];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    // Simulate API call
    setTimeout(() => {
      setRefreshing(false);
    }, 2000);
  };

  const handleExport = () => {
    setMenuAnchor(null);
    // Export functionality would be implemented here
    console.log('Exporting analytics data...');
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Analytics & Reports
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              size="small"
            >
              Filters
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={refreshing}
              size="small"
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <IconButton
              onClick={handleMenuOpen}
              size="small"
            >
              <MoreVert />
            </IconButton>
          </Box>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Comprehensive analytics and reporting dashboard for business intelligence and performance monitoring.
        </Typography>

        {/* Date Range Selector */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
          <TextField
            label="Start Date"
            type="date"
            value={dateRange.start.toISOString().split('T')[0]}
            onChange={(e) => setDateRange(prev => ({ ...prev, start: new Date(e.target.value) }))}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            value={dateRange.end.toISOString().split('T')[0]}
            onChange={(e) => setDateRange(prev => ({ ...prev, end: new Date(e.target.value) }))}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        {/* Key Metrics Overview */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {keyMetrics.map((metric, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: `${metric.color}.main`, mr: 2 }}>
                      {metric.icon}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {metric.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {metric.title}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip
                      size="small"
                      icon={metric.trend === 'up' ? <TrendingUp /> : <TrendingDown />}
                      label={`${metric.change > 0 ? '+' : ''}${metric.change}%`}
                      color={metric.trend === 'up' ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Quick Stats */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Quick Overview
          </Typography>
          <Grid container spacing={3}>
            {quickStats.map((stat, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    {stat.value}
                  </Typography>
                  <Typography variant="subtitle1">
                    {stat.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.description}
                  </Typography>
                </Box>
                {index < quickStats.length - 1 && (
                  <Divider orientation="vertical" flexItem sx={{ ml: 2, display: { xs: 'none', md: 'block' } }} />
                )}
              </Grid>
            ))}
          </Grid>
        </Paper>

        {refreshing && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
          </Box>
        )}
      </Box>

      {/* Main Content Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange} 
            aria-label="analytics tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab 
              icon={<DashboardIcon />} 
              label="Performance Dashboard" 
              {...a11yProps(0)} 
              iconPosition="start"
            />
            <Tab 
              icon={<ProjectIcon />} 
              label="Project Reports" 
              {...a11yProps(1)} 
              iconPosition="start"
            />
            <Tab 
              icon={<TeamIcon />} 
              label="Team Productivity" 
              {...a11yProps(2)} 
              iconPosition="start"
            />
            <Tab 
              icon={<FinancialIcon />} 
              label="Financial Reports" 
              {...a11yProps(3)} 
              iconPosition="start"
            />
          </Tabs>
        </Box>

        <TabPanel value={currentTab} index={0}>
          <PerformanceDashboard dateRange={dateRange} />
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <ProjectReports dateRange={dateRange} />
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <TeamProductivity dateRange={dateRange} />
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <FinancialReports dateRange={dateRange} />
        </TabPanel>
      </Paper>

      {/* Export Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleExport}>
          <Download sx={{ mr: 1 }} />
          Export to PDF
        </MenuItem>
        <MenuItem onClick={handleExport}>
          <Download sx={{ mr: 1 }} />
          Export to Excel
        </MenuItem>
        <MenuItem onClick={handleExport}>
          <Download sx={{ mr: 1 }} />
          Export to CSV
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default AnalyticsAndReportsPage;